/** @jsxImportSource @emotion/react */
import React from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useTranslation } from 'react-i18next'
import { formatCurrency, formatInt } from './dashboard-helpers'
import { DashboardChartCard } from './dashboard-chart-card'

export type TopListDatum = {
    name: string
    count: number
    revenue: number
}

type Props = {
    title: string
    data: TopListDatum[]
    color: string
}

const truncate = (value: string, max = 22): string => (value.length > max ? `${value.slice(0, max)}…` : value)

const TopListTooltip: React.FC<any> = ({ active, payload }) => {
    const { t } = useTranslation()
    if (!active || !payload || payload.length === 0) return null
    const datum: TopListDatum = payload[0].payload
    return (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 4, padding: '8px 12px' }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{datum.name}</div>
            <div style={{ fontSize: 12 }}>{t('TEXT_TOTAL_REPORTS', 'Rapporter levert')}: {formatInt(datum.count)}</div>
            <div style={{ fontSize: 12 }}>{t('TEXT_TOTAL_REVENUE', 'Omsetning')}: {formatCurrency(datum.revenue)}</div>
        </div>
    )
}

export const TopListChart: React.FC<Props> = ({ title, data, color }) => {
    const { t } = useTranslation()
    return (
        <DashboardChartCard title={title} isEmpty={data.length === 0} emptyText={t('TEXT_NO_DATA', 'Ingen data for valgt periode')}>
            <ResponsiveContainer width="100%" height={Math.max(240, data.length * 56)}>
                <BarChart layout="vertical" data={data} margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                    <YAxis
                        type="category"
                        dataKey="name"
                        width={150}
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value: string) => truncate(value)}
                    />
                    <Tooltip content={<TopListTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                    <Bar dataKey="count" fill={color} radius={[0, 4, 4, 0]} />
                </BarChart>
            </ResponsiveContainer>
        </DashboardChartCard>
    )
}
